import React, { useState, useEffect, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Grid, Chip, Box, Button, Skeleton, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import Expandable from '../components/common/Expandable';
import { getAnimeDetails, getAnimeStatus } from '../services/animeApi';
import { AnimeDetail, AnimeRelation, AnimeCharacterVoiceActor, AnimeStaffEntry, AnimeVideo } from '../types/Anime';
import AnimeStatusButton from '../components/AnimeStatusButton';

// ============================================================
// Styled Components (GameDetailsPage準拠)
// ============================================================

const FullWidthGlassSection = styled('div')({
  position: 'relative',
  width: '100vw',
  left: '50%',
  right: '50%',
  marginTop: '-240px',
  marginLeft: '-50vw',
  marginRight: '-50vw',
  paddingTop: '64px',
  zIndex: 1,
  '@media (max-width: 899px)': {
    marginTop: '-164px',
    paddingTop: '56px',
  }
});

const BackgroundImage = styled('div')<{ image: string }>(({ image }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'blur(35px)',
  opacity: 0.1,
  zIndex: -1,
}));

const GlassOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(18, 18, 18, 0)',
  zIndex: -1,
});

const ContentWrapper = styled(Container)({
  position: 'relative',
  zIndex: 1,
  overflow: 'visible',
  '@media (max-width: 899px)': {
    paddingTop: '0 !important'
  }
});

const NormalSection = styled('div')({
  position: 'relative',
  zIndex: 2,
  backgroundColor: '#121212',
});

const HeroImageSection = styled('div')({
  position: 'relative',
  width: '100%',
  height: '464px',
  marginTop: '-64px',
  marginBottom: '-100px',
  overflow: 'hidden',
  '@media (max-width: 899px)': {
    height: '400px',
    marginTop: '-56px',
    marginBottom: '-60px',
  }
});

const HeroGradient = styled('div')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '200px',
  background: 'linear-gradient(to bottom, rgba(18, 18, 18, 0) 0%, rgba(18, 18, 18, 0.85) 70%, rgba(18, 18, 18, 1) 100%)',
  pointerEvents: 'none',
});

const HeroFallbackGradient = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, #1a237e 0%, #121212 100%)',
  opacity: 0.5,
});

const HeroBlurredCover = styled('div')<{ image: string }>(({ image }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'blur(25px)',
  opacity: 0.35,
}));

const CoverImageSection = styled('div')({
  position: 'relative',
  width: '100%',
  maxWidth: '400px',
  marginBottom: '0.5rem',
  '@media (max-width: 899px)': {
    width: '35%',
    minWidth: '120px',
    maxWidth: '120px',
    marginTop: '-80px',
    marginLeft: '0px',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
  }
});

const CoverImage = styled('img')({
  width: '100%',
  height: 'auto',
  display: 'block',
  borderRadius: '4px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  '@media (max-width: 899px)': {
    aspectRatio: '3/4',
    objectFit: 'cover',
  }
});

// ============================================================
// Helper Components
// ============================================================

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle1" sx={{
    fontSize: { xs: '0.8rem', sm: '0.9rem' },
    fontWeight: 600,
    mb: 1.5,
    color: '#ffffffd9',
  }}>
    {children}
  </Typography>
);

const MetaChip: React.FC<{ label: string }> = ({ label }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      fontSize: { xs: '0.6rem', sm: '0.65rem' },
      height: { xs: '20px', sm: '22px' },
      bgcolor: 'rgba(255, 255, 255, 0.08)',
      color: '#ffffffcc',
    }}
  />
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
    <Typography variant="caption" sx={{ color: '#a7a7a7', minWidth: '60px', fontSize: '0.65rem' }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ color: '#ffffffcc', fontSize: '0.65rem' }}>
      {value}
    </Typography>
  </Box>
);

const getScoreColor = (score: number) => {
  if (score >= 8.5) return '#4CAF50';
  if (score >= 7.5) return '#8BC34A';
  if (score >= 6.5) return '#CDDC39';
  if (score >= 5.5) return '#FFC107';
  if (score >= 4.5) return '#FF9800';
  return '#F44336';
};

const getStatusLabel = (key: string) => {
  const map: Record<string, string> = {
    s1: '視聴済み', s2: '視聴中', s3: '中断', s4: 'リタイア', s5: 'ウォッチリスト'
  };
  return map[key] || key;
};

const relationLabelMap: Record<string, string> = {
  'Sequel': '続編',
  'Prequel': '前日譚',
  'Side Story': 'サイドストーリー',
  'Summary': '総集編',
  'Full Story': '完全版',
  'Parent Story': '親ストーリー',
  'Alternative Setting': '別設定',
  'Alternative Version': '別バージョン',
  'Spin-Off': 'スピンオフ',
  'Adaptation': '原作/アダプテーション',
  'Character': 'キャラクター',
  'Other': 'その他',
};

const seasonLabelMap: Record<string, string> = {
  'spring': '春', 'summer': '夏', 'fall': '秋', 'winter': '冬'
};

const statusLabelMap: Record<string, string> = {
  'Finished Airing': '放送終了',
  'Currently Airing': '放送中',
  'Not yet aired': '放送前',
};

// ============================================================
// Main Component
// ============================================================

const AnimeDetailsPage: React.FC = () => {
  const { mal_id } = useParams<{ mal_id: string }>();
  const [anime, setAnime] = useState<AnimeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState<number | null>(null);

  useEffect(() => {
    const fetchAnimeDetails = async () => {
      setLoading(true);
      setAnime(null);
      setUserStatus(null);
      try {
        if (mal_id) {
          const malIdNum = parseInt(mal_id, 10);
          const data = await getAnimeDetails(malIdNum);
          setAnime(data);

          // ログイン中ならユーザーステータス取得
          const token = localStorage.getItem('token');
          if (token) {
            try {
              const statusData = await getAnimeStatus(malIdNum);
              setUserStatus(statusData.status);
            } catch {}
          }
        }
      } catch (error) {
        console.error('アニメ詳細の取得に失敗しました', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnimeDetails();
    window.scrollTo(0, 0);
  }, [mal_id]);

  // ============================================================
  // Loading
  // ============================================================
  if (loading) {
    return (
      <>
        <HeroImageSection>
          <HeroFallbackGradient />
          <HeroGradient />
        </HeroImageSection>
        <FullWidthGlassSection>
          <ContentWrapper maxWidth="lg" sx={{ px: 1.5, pt: { xs: 2, md: 4 }, pb: 4 }}>
            <Grid container spacing={{ xs: 2, md: 4 }}>
              <Grid item xs={12} md={3.5}>
                <CoverImageSection>
                  <Skeleton variant="rectangular" sx={{ width: '100%', height: 0, paddingBottom: '133.33%', borderRadius: '4px' }} />
                </CoverImageSection>
              </Grid>
              <Grid item xs={12} md={8.5}>
                <Skeleton width="80%" height={32} />
                <Skeleton width="60%" height={24} sx={{ mt: 1 }} />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Skeleton width={60} height={24} />
                  <Skeleton width={60} height={24} />
                  <Skeleton width={60} height={24} />
                </Box>
                <Skeleton width="100%" height={100} sx={{ mt: 2 }} />
              </Grid>
            </Grid>
          </ContentWrapper>
        </FullWidthGlassSection>
        <NormalSection>
          <Container maxWidth="lg" sx={{ px: 1.5, pt: 2, pb: 4, minHeight: '50vh' }}>
            <Skeleton width="100%" height={200} />
          </Container>
        </NormalSection>
      </>
    );
  }

  if (!anime) {
    return (
      <Container maxWidth="lg" sx={{ pt: 10, textAlign: 'center' }}>
        <Typography>アニメ情報が見つかりません。</Typography>
      </Container>
    );
  }

  // ============================================================
  // Data
  // ============================================================
  const displayTitle = anime.title_japanese || anime.title;
  const subTitle = anime.title_japanese ? (anime.title_english || anime.title) : anime.title_english;
  const scoreNum = anime.score ? Number(anime.score) : null;
  const imageUrl = anime.image_url
    ? anime.image_url.replace(/(\.\w+)$/, 'l$1')
    : '/image/no_image.png';

  const formatAiredDate = () => {
    if (!anime.aired_from) return '不明';
    const from = new Date(anime.aired_from).toLocaleDateString('ja-JP');
    if (!anime.aired_to) return anime.status === 'Currently Airing' ? `${from} - 放送中` : from;
    return `${from} - ${new Date(anime.aired_to).toLocaleDateString('ja-JP')}`;
  };

  // 関連作品をrelation種別でグループ化
  const groupedRelations = anime.relations.reduce<Record<string, AnimeRelation[]>>((acc, rel) => {
    const key = rel.relation;
    if (!acc[key]) acc[key] = [];
    acc[key].push(rel);
    return acc;
  }, {});

  // キャラ&声優 (日本語声優優先、キャラ単位でグループ化)
  const jaVoiceActors = anime.character_voice_actors.filter(cv => cv.language === 'Japanese');
  const source = jaVoiceActors.length > 0 ? jaVoiceActors : anime.character_voice_actors;
  const charMap = new Map<number, { cv: AnimeCharacterVoiceActor; vaNames: string[] }>();
  source.forEach(cv => {
    const vaName = cv.person.given_name && cv.person.family_name
      ? `${cv.person.family_name} ${cv.person.given_name}`
      : cv.person.name;
    const existing = charMap.get(cv.character.character_id);
    if (existing) {
      existing.vaNames.push(vaName);
    } else {
      charMap.set(cv.character.character_id, { cv, vaNames: [vaName] });
    }
  });
  const displayCharacters = Array.from(charMap.values());

  // 動画 (promo優先)
  const promoVideos = anime.videos.filter(v => v.video_type === 'promo');
  const musicVideos = anime.videos.filter(v => v.video_type === 'music_video');

  return (
    <>
      <Helmet>
        <title>
          {anime.title_japanese
            ? `${anime.title_japanese} / ${anime.title}${anime.year ? ` (${anime.year})` : ''} | PULSE`
            : `${anime.title}${anime.year ? ` (${anime.year})` : ''} | PULSE`
          }
        </title>
      </Helmet>

      {/* ============================================================ */}
      {/* Hero Section */}
      {/* ============================================================ */}
      <HeroImageSection>
        {anime.image_url ? (
          <>
            <HeroBlurredCover image={anime.image_url} />
            <HeroGradient />
          </>
        ) : (
          <>
            <HeroFallbackGradient />
            <HeroGradient />
          </>
        )}
      </HeroImageSection>

      {/* ============================================================ */}
      {/* Glass Section - Main Info */}
      {/* ============================================================ */}
      <FullWidthGlassSection>
        <BackgroundImage image={imageUrl} />
        <GlassOverlay />
        <ContentWrapper maxWidth="lg" sx={{
          px: 1.5,
          pt: { xs: 2, md: 4 },
          pb: { xs: 2, md: 4 }
        }}>
          <Grid container spacing={{ xs: 2, md: 4 }} sx={{
            '@media (max-width: 899px)': { flexDirection: 'column' }
          }}>
            {/* Left: Cover Image */}
            <Grid item xs={12} md={3.5} sx={{
              '@media (max-width: 899px)': { flex: '0 0 auto', maxWidth: '100%', paddingTop: 0 }
            }}>
              <CoverImageSection>
                <CoverImage src={imageUrl} alt={displayTitle} />
                {/* Status Button */}
                <Box sx={{ mt: 1, display: { xs: 'none', md: 'block' } }}>
                  <AnimeStatusButton
                    malId={anime.mal_id}
                    initialStatus={userStatus}
                    onStatusChange={(newStatus) => setUserStatus(newStatus)}
                  />
                </Box>
                {/* MALリンク */}
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    component="a"
                    href={`https://myanimelist.net/anime/${anime.mal_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      textTransform: 'none',
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      color: '#ffffffe8',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' }
                    }}
                  >
                    MyAnimeList
                  </Button>
                </Box>
              </CoverImageSection>
            </Grid>

            {/* Right: Metadata */}
            <Grid item xs={12} md={8.5} sx={{
              '@media (max-width: 899px)': { paddingTop: 0 }
            }}>
              {/* Title */}
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '0.9rem', sm: '1.3rem' } }}>
                {displayTitle}
                {anime.year && (
                  <Typography component="span" color="text.secondary" sx={{ ml: 1.5, fontSize: '1em' }}>
                    ({anime.year})
                  </Typography>
                )}
              </Typography>
              {subTitle && subTitle !== displayTitle && (
                <Typography variant="h6" component="h2" sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  lineHeight: 1.2,
                  mb: 1
                }}>
                  {subTitle}
                </Typography>
              )}

              {/* Mobile Status Button */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 1 }}>
                <AnimeStatusButton
                  malId={anime.mal_id}
                  initialStatus={userStatus}
                  onStatusChange={(newStatus) => setUserStatus(newStatus)}
                />
              </Box>

              {/* Genre (dot-separated) */}
              {anime.genres.length > 0 && (
                <Typography variant="body2" sx={{
                  color: '#a7a7a7',
                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                  mb: 1,
                  '& > span::before': { content: '"・"', color: 'rgba(255, 255, 255, 0.3)' }
                }}>
                  {anime.genres.map((g) => (
                    <Box component="span" key={g.genre_id}>{g.name}</Box>
                  ))}
                </Typography>
              )}

              {/* Meta Chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {anime.type && <MetaChip label={anime.type} />}
                {anime.episodes && <MetaChip label={`${anime.episodes}話`} />}
                {anime.source && <MetaChip label={anime.source} />}
                {anime.duration && <MetaChip label={anime.duration} />}
                {anime.rating && <MetaChip label={anime.rating} />}
                {anime.status && <MetaChip label={statusLabelMap[anime.status] || anime.status} />}
              </Box>

              {/* Score Display (inline) */}
              {scoreNum && (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: getScoreColor(scoreNum), lineHeight: 1 }}>
                      {scoreNum.toFixed(2)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>
                      / 10
                    </Typography>
                  </Box>
                  {anime.scored_by && (
                    <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>
                      ({anime.scored_by.toLocaleString()}人)
                    </Typography>
                  )}
                  {anime.rank && (
                    <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>
                      #{anime.rank}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Synopsis */}
              {anime.synopsis && (
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <Expandable maxLines={5} expandButtonStyle="icon">
                    <Typography variant="body2" sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      lineHeight: 1.25,
                    }}>
                      {anime.synopsis}
                    </Typography>
                  </Expandable>
                </Box>
              )}

              {/* Info Grid */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Studios */}
                    <Typography variant="subtitle1" sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>
                      スタジオ
                    </Typography>
                    {anime.studios.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {anime.studios.map((s) => (
                          <Chip key={s.studio_id} label={s.name} size="small" sx={{
                            fontSize: { xs: '0.675rem', sm: '0.7rem' },
                            height: { xs: '20px', sm: '24px' },
                            bgcolor: 'rgba(255, 255, 255, 0.08)',
                            color: '#ffffff',
                          }} />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.675rem', color: '#a7a7a7' }}>情報なし</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {/* Producers */}
                    <Typography variant="subtitle1" sx={{ color: '#FFFFFFB3', fontWeight: 400, fontSize: '0.7rem' }}>
                      プロデューサー
                    </Typography>
                    {anime.producer_details.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {anime.producer_details.map((p) => (
                          <Chip key={`${p.producer_id}-${p.producer_type}`} label={p.name_ja || p.name} size="small" sx={{
                            fontSize: { xs: '0.675rem', sm: '0.7rem' },
                            height: { xs: '20px', sm: '24px' },
                            bgcolor: 'rgba(255, 255, 255, 0.08)',
                            color: '#ffffff',
                          }} />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.675rem', color: '#a7a7a7' }}>情報なし</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Broadcast & Aired Info */}
              <Box sx={{ mt: 2 }}>
                <InfoRow label="放送期間" value={formatAiredDate()} />
                {anime.season && anime.year && (
                  <InfoRow label="シーズン" value={`${anime.year}年 ${seasonLabelMap[anime.season] || anime.season}`} />
                )}
                {anime.broadcast_day && anime.broadcast_time && (
                  <InfoRow label="放送日時" value={`${anime.broadcast_day} ${anime.broadcast_time}`} />
                )}
              </Box>

              {/* External Links */}
              <Box sx={{
                mt: 2,
                display: 'flex',
                gap: { xs: 0.75, sm: 1 },
                '& .MuiButton-root': {
                  minWidth: 'auto',
                  borderRadius: { xs: '6px', sm: '8px' },
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textTransform: 'none',
                  padding: { xs: '2px 12px', sm: '1px 12px' },
                  color: '#ffffffe8',
                  fontSize: { xs: '0.675rem', sm: '0.7rem' },
                  fontWeight: 500,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)', transform: 'translateY(-1px)' }
                }
              }}>
                <Button variant="contained" size="small" component="a"
                  href={`https://www.amazon.co.jp/s?k=${encodeURIComponent(displayTitle)}&i=instant-video&tag=pulse-db-22`}
                  target="_blank" rel="noopener noreferrer"
                >
                  Amazon
                </Button>
                <Button variant="contained" size="small" component="a"
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(displayTitle)}+アニメ+PV`}
                  target="_blank" rel="noopener noreferrer"
                >
                  YouTube
                </Button>
              </Box>
            </Grid>
          </Grid>
        </ContentWrapper>
      </FullWidthGlassSection>

      {/* ============================================================ */}
      {/* Normal Section - Below the fold */}
      {/* ============================================================ */}
      <NormalSection>
        <Container maxWidth="lg" sx={{ px: 1.5, pt: { xs: 2, md: 2 }, pb: 4 }}>
          <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={8}>

              {/* Relations */}
              {Object.keys(groupedRelations).length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>関連作品</SectionTitle>
                  {Object.entries(groupedRelations).map(([relation, items]) => (
                    <Box key={relation} sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ color: '#a7a7a7', fontSize: '0.65rem', mb: 0.5, display: 'block' }}>
                        {relationLabelMap[relation] || relation}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {items.map((rel) => (
                          <Box key={`${rel.related_mal_id}-${rel.relation}`} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: '6px',
                            bgcolor: 'rgba(255, 255, 255, 0.03)',
                            border: '1px solid rgba(255, 255, 255, 0.06)',
                            transition: 'all 0.2s ease',
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.06)' }
                          }}>
                            {rel.related_type === 'anime' ? (
                              <Box component={Link} to={`/anime/${rel.related_mal_id}`} sx={{
                                textDecoration: 'none', color: '#ffffffcc', fontSize: '0.7rem',
                                '&:hover': { color: '#ffffff' }
                              }}>
                                {rel.related_name || `MAL ID: ${rel.related_mal_id}`}
                              </Box>
                            ) : (
                              <Box component="a"
                                href={`https://myanimelist.net/manga/${rel.related_mal_id}`}
                                target="_blank" rel="noopener noreferrer"
                                sx={{
                                  textDecoration: 'none', color: '#a7a7a7', fontSize: '0.7rem',
                                  '&:hover': { color: '#ffffff' }
                                }}
                              >
                                {rel.related_name || `MAL ID: ${rel.related_mal_id}`}
                                <Typography component="span" sx={{ fontSize: '0.6rem', color: '#666', ml: 0.5 }}>
                                  (manga)
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Videos (PV/Promo) */}
              {promoVideos.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>PV・トレーラー</SectionTitle>
                  <Expandable maxHeight={400} expandButtonStyle="pill" expandLabel="すべて表示" collapseLabel="折りたたむ">
                    <Grid container spacing={1.5}>
                      {promoVideos.map((video, idx) => {
                        if (!video.youtube_id) return null;
                        const ytId = video.youtube_id;
                        return (
                          <Grid item xs={12} sm={6} key={idx}>
                            <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: '6px', overflow: 'hidden', bgcolor: '#000' }}>
                              <iframe
                                src={`https://www.youtube.com/embed/${ytId}`}
                                title={video.title || 'PV'}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                              />
                            </Box>
                            {video.title && (
                              <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7', mt: 0.5 }}>
                                {video.title}
                              </Typography>
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Expandable>
                </Box>
              )}

              {/* Music Videos (OP/ED) */}
              {musicVideos.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>主題歌</SectionTitle>
                  <Expandable maxHeight={300} expandButtonStyle="pill" expandLabel="すべて表示" collapseLabel="折りたたむ">
                    <Grid container spacing={1.5}>
                      {musicVideos.map((video, idx) => {
                        if (!video.youtube_id) return null;
                        const ytId = video.youtube_id;
                        return (
                          <Grid item xs={12} sm={6} key={idx}>
                            <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: '6px', overflow: 'hidden', bgcolor: '#000' }}>
                              <iframe
                                src={`https://www.youtube.com/embed/${ytId}`}
                                title={video.title || 'Music Video'}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                              />
                            </Box>
                            {video.title && (
                              <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7', mt: 0.5 }}>
                                {video.title}
                              </Typography>
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Expandable>
                </Box>
              )}

              {/* Characters & Voice Actors */}
              {displayCharacters.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>キャラクター・声優</SectionTitle>
                  <Expandable maxHeight={480} expandButtonStyle="pill" expandLabel="すべて表示" collapseLabel="折りたたむ">
                    <Grid container spacing={1}>
                      {displayCharacters.map(({ cv, vaNames }, idx) => (
                        <Grid item xs={4} sm={3} md={2.4} key={cv.character.character_id}>
                          <Box sx={{
                            borderRadius: '6px',
                            bgcolor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            overflow: 'hidden',
                          }}>
                            <Box component="img"
                              src={cv.character.image_url || '/image/no_image.png'}
                              alt={cv.character.name}
                              sx={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block' }}
                            />
                            <Box sx={{ p: 0.75 }}>
                              <Typography noWrap sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#ffffffcc' }}>
                                {cv.character.name_kanji || cv.character.name}
                              </Typography>
                              <Typography noWrap sx={{ fontSize: '0.6rem', color: '#a7a7a7' }}>
                                {vaNames.join(', ')}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Expandable>
                </Box>
              )}

              {/* Staff */}
              {anime.staff.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>スタッフ</SectionTitle>
                  <Expandable maxHeight={250} expandButtonStyle="pill" expandLabel="すべて表示" collapseLabel="折りたたむ">
                    <Grid container spacing={1}>
                      {anime.staff.map((s, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 0.75,
                            borderRadius: '4px',
                            bgcolor: 'rgba(255, 255, 255, 0.02)',
                          }}>
                            <Box component="img"
                              src={s.person.image_url || '/image/no_image.png'}
                              alt={s.person.name}
                              sx={{ width: 32, height: 44, objectFit: 'cover', borderRadius: '3px', flexShrink: 0 }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography noWrap sx={{ fontSize: '0.65rem', fontWeight: 500, color: '#ffffffcc' }}>
                                {s.person.given_name && s.person.family_name
                                  ? `${s.person.family_name} ${s.person.given_name}`
                                  : s.person.name
                                }
                              </Typography>
                              <Typography sx={{ fontSize: '0.6rem', color: '#a7a7a7' }}>
                                {s.position}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Expandable>
                </Box>
              )}

              {/* Background */}
              {anime.background && (
                <Box sx={{ mb: 4 }}>
                  <SectionTitle>背景</SectionTitle>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.4 }}>
                    {anime.background}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Right Column - Stats Sidebar */}
            <Grid item xs={12} md={4}>
              <Box sx={{ position: 'sticky', top: '80px' }}>
                {/* Stats Card */}
                <Box sx={{
                  p: 2,
                  borderRadius: '8px',
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  mb: 2,
                }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 1.5, color: '#ffffffd9' }}>
                    統計
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {scoreNum && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>スコア</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: getScoreColor(scoreNum), fontWeight: 600 }}>
                          {scoreNum.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    {anime.rank && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>ランキング</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ffffffcc' }}>#{anime.rank}</Typography>
                      </Box>
                    )}
                    {anime.popularity && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>人気度</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ffffffcc' }}>#{anime.popularity}</Typography>
                      </Box>
                    )}
                    {anime.members !== undefined && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>メンバー数</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ffffffcc' }}>{anime.members.toLocaleString()}</Typography>
                      </Box>
                    )}
                    {anime.favorites !== undefined && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#a7a7a7' }}>お気に入り</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#ffffffcc' }}>{anime.favorites.toLocaleString()}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Status Counts */}
                {anime.status_counts && anime.status_counts.total > 0 && (
                  <Box sx={{
                    p: 2,
                    borderRadius: '8px',
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 1.5, color: '#ffffffd9' }}>
                      ユーザーステータス
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {(['s1', 's2', 's3', 's4', 's5'] as const).map((key) => {
                        const count = anime.status_counts![key];
                        const total = anime.status_counts!.total;
                        const pct = total > 0 ? (count / total) * 100 : 0;
                        return (
                          <Box key={key}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                              <Typography sx={{ fontSize: '0.6rem', color: '#a7a7a7' }}>{getStatusLabel(key)}</Typography>
                              <Typography sx={{ fontSize: '0.6rem', color: '#ffffffcc' }}>{count}</Typography>
                            </Box>
                            <Box sx={{ width: '100%', height: 3, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 }}>
                              <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 1 }} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </NormalSection>
    </>
  );
};

export default memo(AnimeDetailsPage);
